/**
 * URL Parameter Loader Component
 * Handles loading products and base model from URL parameters
 * Supports both ID-based and SKU-based URLs
 *
 * @package
 */

import { useEffect, useRef } from '@wordpress/element';
import { getProductIdFromSku, isNumericId } from '../helpers/getProductIdsFromSkus';

const UrlParameterLoader = ( {
	productIds,
	baseModelId,
	baseModels,
	vehicleConfigId,
	hasBundle,
	setSelectedBaseModel,
	setSelectedProducts,
	onBaseModelUnresolved = () => {},
} ) => {
	const loadedParamsRef = useRef( null );

	useEffect( () => {
		// Create a stable key from the parameters to check if they've changed
		const paramsKey = JSON.stringify( {
			baseModelId,
			productIds: productIds?.join( ',' ),
			vehicleConfigId,
			hasBundle,
		} );

		// If we've already loaded these exact parameters, skip
		if ( loadedParamsRef.current === paramsKey ) {
			return;
		}

		loadedParamsRef.current = paramsKey;

		// Only set base model if no bundle is being loaded (bundle takes priority).
		if ( baseModelId && ! hasBundle ) {
			// Validate productId against the already-processed baseModels list from config.
			const applyIfInConfig = productId => {
				const exists = ( baseModels || [] ).some( m => {
					const pid = typeof m === 'object' ? m.product_id : parseInt( m, 10 );
					return pid === productId;
				} );
				if ( exists ) {
					setSelectedBaseModel( productId );
				} else {
					onBaseModelUnresolved();
				}
			};

			if ( isNumericId( baseModelId ) ) {
				applyIfInConfig( parseInt( baseModelId, 10 ) );
			} else {
				getProductIdFromSku( baseModelId )
					.then( productId => {
						if ( productId ) {
							applyIfInConfig( productId );
						} else {
							onBaseModelUnresolved();
						}
					} )
					.catch( onBaseModelUnresolved );
			}
		}

		// Products can be added even if a bundle is loaded (bundle + additional products).
		if ( productIds && productIds.length > 0 ) {
			const numericIds = [];
			const skus = [];

			productIds.forEach( value => {
				if ( isNumericId( value ) ) {
					numericIds.push( parseInt( value, 10 ) );
				} else {
					skus.push( value );
				}
			} );

			if ( numericIds.length > 0 ) {
				setSelectedProducts( currentProducts => {
					const merged = [ ...currentProducts ];
					numericIds.forEach( id => {
						if ( id && ! merged.includes( id ) ) {
							merged.push( id );
						}
					} );
					return merged;
				} );
			}

			if ( skus.length > 0 ) {
				Promise.all( skus.map( sku => getProductIdFromSku( sku ) ) ).then( ids => {
					const validIds = ids.filter( id => id !== null && ! isNaN( id ) );
					if ( validIds.length > 0 ) {
						setSelectedProducts( currentProducts => {
							const merged = [ ...currentProducts ];
							validIds.forEach( id => {
								if ( id && ! merged.includes( id ) ) {
									merged.push( id );
								}
							} );
							return merged;
						} );
					}
				} );
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		// Only depend on the stringified values to prevent re-runs from array reference changes
		baseModelId,
		productIds?.join( ',' ),
		vehicleConfigId,
		hasBundle,
		// setSelectedBaseModel and setSelectedProducts are stable, but we include them for completeness
		setSelectedBaseModel,
		setSelectedProducts,
	] );

	return null;
};

export default UrlParameterLoader;
